import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { QrCode, ScanLine, Send, X, Loader2, Check, Inbox } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Share-Leiste für teilbare Listen (Tagesgeschäft, Auffüllen, ...). Nur Admin/SL/Superadmin.
export const TagesgeschaeftShareBar = ({ locationId, onSent, contentType = 'tagesgeschaeft', listLabel = 'Liste', locations = [], categoryId = null, subjectName = null, subjectIcon = null, variant = 'full' }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // 'send' | 'qr' | 'scan'
  const [srcLoc, setSrcLoc] = useState(locationId || (locations[0]?.id || ''));
  const effLoc = locationId || srcLoc;
  const showLocPicker = !categoryId && !locationId && locations.length > 1;
  const canShare = categoryId ? true : !!effLoc;
  // Kundennummer-Versand
  const [numSuffix, setNumSuffix] = useState('');
  const [validState, setValidState] = useState(null); // null | 'checking' | true | false
  const [sending, setSending] = useState(false);
  const debounceRef = useRef(null);
  // QR-Anzeige
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  // Erhaltene Vorlagen (persistenter, status-basierter Badge)
  const [pendingCount, setPendingCount] = useState(0);

  React.useEffect(() => {
    axios.get(`${API}/api/list-transfers/received/pending-count`, { headers: authH() })
      .then(r => setPendingCount(r.data?.count || 0)).catch(() => {});
  }, []);

  const fullNumber = `AF${numSuffix.replace(/[^0-9]/g, '')}`;

  const close = () => { setMode(null); setNumSuffix(''); setValidState(null); setQrData(null); };

  const onNumChange = (v) => {
    const digits = v.replace(/[^0-9]/g, '');
    setNumSuffix(digits);
    setValidState(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (digits.length < 4) return;
    setValidState('checking');
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/list-transfers/validate-customer-number`,
          { params: { number: `AF${digits}` }, headers: authH() });
        setValidState(r.data?.valid === true);
      } catch { setValidState(false); }
    }, 400);
  };

  const handleSend = async () => {
    if (validState !== true) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/list-transfers/send`,
        { content_type: contentType, location_id: effLoc || undefined, category_id: categoryId || undefined, recipient_customer_number: fullNumber },
        { headers: authH() });
      toast.success(`${subjectName ? `„${subjectName}"` : 'Liste'} an ${fullNumber} gesendet (${r.data.item_count} Einträge).`);
      close(); onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Senden fehlgeschlagen');
    } finally { setSending(false); }
  };

  const openQr = async () => {
    setMode('qr'); setQrLoading(true); setQrData(null);
    try {
      const r = await axios.post(`${API}/api/list-transfers/create-qr`,
        { content_type: contentType, location_id: effLoc || undefined, category_id: categoryId || undefined }, { headers: authH() });
      setQrData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'QR-Erstellung fehlgeschlagen');
      close();
    } finally { setQrLoading(false); }
  };

  const handleScan = (results) => {
    const raw = results?.[0]?.rawValue;
    if (!raw) return;
    let token = null;
    try { token = new URL(raw).searchParams.get('token'); } catch { token = null; }
    if (!token && raw.includes('token=')) token = raw.split('token=')[1].split('&')[0];
    if (token) { close(); navigate(`/tagesgeschaeft/empfangen?token=${token}`); }
    else toast.error('Kein gültiger AllFlowX-QR-Code');
  };

  const subjectChip = subjectName ? (
    <div className="flex items-center gap-2 rounded-lg bg-muted/60 px-3 py-2" data-testid="share-subject">
      {subjectIcon && <span className="text-xl">{subjectIcon}</span>}
      <span className="text-sm font-semibold truncate">{subjectName}</span>
    </div>
  ) : null;

  return (
    <>
      {variant === 'compact' ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="share-bar-compact">
          <span className="text-xs font-medium text-blue-800 dark:text-blue-300">Teilen:</span>
          <Button size="sm" variant="outline" className="gap-1.5 bg-white dark:bg-gray-800" onClick={() => setMode('send')} data-testid="share-sendnumber-btn">
            <Send className="h-4 w-4" /> An Kundennummer
          </Button>
          <Button size="sm" variant="outline" className="gap-1.5 bg-white dark:bg-gray-800" onClick={openQr} data-testid="share-showqr-btn">
            <QrCode className="h-4 w-4" /> QR-Code
          </Button>
        </div>
      ) : variant === 'receive' ? (
        <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-blue-50/60 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30" data-testid="share-bar-receive">
          <Button size="sm" variant="outline" className="gap-1.5 bg-white dark:bg-gray-800" onClick={() => setMode('scan')} data-testid="share-scan-btn">
            <ScanLine className="h-4 w-4" /> QR scannen
          </Button>
          <Button size="sm" variant="ghost" className="gap-1.5 relative ml-auto" onClick={() => navigate('/tagesgeschaeft/empfangen')} data-testid="share-received-btn">
            <Inbox className="h-4 w-4" /> Erhaltene Vorlagen
            {pendingCount > 0 && (
              <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="share-received-badge">
                {pendingCount > 9 ? '9+' : pendingCount}
              </span>
            )}
          </Button>
        </div>
      ) : (
      <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-blue-50/60 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30" data-testid="share-bar">
        <span className="w-full text-xs font-medium text-blue-800 dark:text-blue-300 mb-1">{categoryId ? 'Kategorie mit Partner teilen' : 'Liste mit Partner teilen'}</span>
        {showLocPicker && (
          <select value={srcLoc} onChange={e => setSrcLoc(e.target.value)}
            className="w-full mb-1 text-sm rounded-lg border border-border bg-white dark:bg-gray-800 px-2 py-1.5" data-testid="share-source-location">
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
        <Button size="sm" variant="outline" className="gap-1.5 bg-white dark:bg-gray-800" onClick={() => setMode('scan')} data-testid="share-scan-btn">
          <ScanLine className="h-4 w-4" /> QR scannen
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5 bg-white dark:bg-gray-800" onClick={openQr} disabled={!canShare} data-testid="share-showqr-btn">
          <QrCode className="h-4 w-4" /> QR ansehen
        </Button>
        <Button size="sm" className="gap-1.5" onClick={() => setMode('send')} disabled={!canShare} data-testid="share-sendnumber-btn">
          <Send className="h-4 w-4" /> An Kundennummer senden
        </Button>
        <Button size="sm" variant="ghost" className="gap-1.5 relative ml-auto" onClick={() => navigate('/tagesgeschaeft/empfangen')} data-testid="share-received-btn">
          <Inbox className="h-4 w-4" /> Erhaltene Vorlagen
          {pendingCount > 0 && (
            <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="share-received-badge">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </Button>
      </div>
      )}

      {mode && createPortal(
        <div className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" style={{ zIndex: 10000 }} onClick={close}>
          <div className="bg-white dark:bg-gray-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl px-5 pt-5 space-y-4"
            style={{ maxHeight: '90dvh', overflowY: 'auto', paddingBottom: 'calc(env(safe-area-inset-bottom) + 20px)' }}
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-base">
                {mode === 'send' && 'An Kundennummer senden'}
                {mode === 'qr' && 'QR-Code ansehen'}
                {mode === 'scan' && 'QR-Code scannen'}
              </h2>
              <button onClick={close} className="p-1 rounded-full hover:bg-muted shrink-0" data-testid="share-modal-close"><X className="h-4 w-4" /></button>
            </div>

            {mode === 'send' && (
              <div className="space-y-3">
                {subjectChip}
                <p className="text-sm text-muted-foreground">{categoryId ? `Die komplette Kategorie inkl. Produktliste wird als Kopie gesendet.` : `Die komplette ${listLabel} dieses Standorts wird als Kopie gesendet.`}</p>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-mono font-semibold text-base text-muted-foreground pointer-events-none">AF</span>
                  <Input value={numSuffix} onChange={e => onNumChange(e.target.value)} placeholder="10014"
                    inputMode="numeric" className="font-mono text-base h-12 pl-11 pr-11" style={{ fontSize: '16px' }} data-testid="share-number-input" autoFocus />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center">
                    {validState === 'checking' && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                    {validState === true && <Check className="h-5 w-5 text-green-600" data-testid="share-number-valid" />}
                    {validState === false && <X className="h-5 w-5 text-red-500" data-testid="share-number-invalid" />}
                  </span>
                </div>
                {validState === false && <p className="text-xs text-red-500">Ungültige Kundennummer</p>}
                {validState === true && <p className="text-xs text-green-600">Gültige Kundennummer</p>}
                <Button className="w-full gap-2 h-12" disabled={validState !== true || sending} onClick={handleSend} data-testid="share-send-confirm">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Liste senden
                </Button>
              </div>
            )}

            {mode === 'qr' && (
              <div className="space-y-3 text-center">
                {subjectChip}
                {qrLoading ? <div className="py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div> : (
                  qrData?.qr_data_url ? (
                    <>
                      <p className="text-sm text-muted-foreground">Ein Kollege scannt diesen Code, um deine {listLabel} ({qrData.item_count} Einträge) zu übernehmen.</p>
                      <img src={qrData.qr_data_url} alt="QR-Code" className="mx-auto w-56 h-56" data-testid="share-qr-image" />
                      <p className="text-[11px] text-muted-foreground">Code bleibt gültig, bis er angenommen oder zurückgezogen wird.</p>
                    </>
                  ) : <p className="text-sm text-red-500">QR-Code konnte nicht erstellt werden.</p>
                )}
              </div>
            )}

            {mode === 'scan' && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Scanne den QR-Code eines Kollegen, um seine Liste zu erhalten.</p>
                <div className="rounded-xl overflow-hidden border border-border" data-testid="share-scanner">
                  <Scanner onScan={handleScan} onError={() => {}} components={{ finder: true }} styles={{ container: { width: '100%' } }} />
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default TagesgeschaeftShareBar;
